import axiosInstance from './axios';
import {
  SiteLanguage,
  SupportedLanguage,
  CreateSiteLanguagePayload,
  UpdateSiteLanguagePayload,
} from 'types/siteLanguage';

/**
 * Helper to get readable language name from an ISO language code (e.g., 'en' -> 'English')
 */
export const getLanguageName = (code: string): string => {
  if (!code) return '';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'language' });
    const name = dn.of(code);
    if (name && name.toLowerCase() !== code.toLowerCase()) {
      return name;
    }
  } catch {
    // Fallback if code is invalid
  }
  return code.toUpperCase();
};

export const siteLanguagesService = {
  /**
   * GET /admin/site-languages/supported
   * List every language code the platform supports
   */
  getSupportedLanguages: async (): Promise<SupportedLanguage[]> => {
    const response = await axiosInstance.get('/admin/site-languages/supported');
    const raw =
      response.data?.data?.languages ??
      response.data?.languages ??
      response.data?.data ??
      response.data;

    if (!Array.isArray(raw)) {
      return [];
    }

    return raw.map((item: unknown) => {
      if (typeof item === 'string') {
        return {
          code: item,
          name: getLanguageName(item),
        };
      }
      const record = item as Record<string, unknown>;
      const code = String(record.code || '');
      const name = String(record.name || getLanguageName(code));
      const nativeName = record.nativeName ? String(record.nativeName) : undefined;
      return { code, name, nativeName };
    });
  },

  /**
   * GET /admin/site-languages
   * List languages configured for a site
   */
  getSiteLanguages: async (siteId: string | number): Promise<SiteLanguage[]> => {
    const response = await axiosInstance.get('/admin/site-languages', {
      params: { siteId },
    });
    const raw =
      response.data?.data?.languages ??
      response.data?.languages ??
      response.data?.data ??
      response.data;
    if (Array.isArray(raw)) {
      return raw as SiteLanguage[];
    }
    if (Array.isArray(raw?.items)) {
      return raw.items as SiteLanguage[];
    }
    return [];
  },

  /**
   * GET /admin/site-languages/{id}
   * Get one site language by id
   */
  getSiteLanguageById: async (id: string | number): Promise<SiteLanguage> => {
    const response = await axiosInstance.get(`/admin/site-languages/${id}`);
    return (response.data?.data ?? response.data) as SiteLanguage;
  },

  /**
   * POST /admin/site-languages
   * Add a language to a site (multipart — optional logo file)
   */
  createSiteLanguage: async (payload: CreateSiteLanguagePayload): Promise<SiteLanguage> => {
    const formData = new FormData();
    formData.append('siteId', String(payload.siteId));
    formData.append('code', payload.code);
    if (payload.name) {
      formData.append('name', payload.name);
    }
    if (payload.isEnabled !== undefined) {
      formData.append('isEnabled', String(payload.isEnabled));
    }
    if (payload.logo) {
      formData.append('logo', payload.logo);
    }

    const response = await axiosInstance.post('/admin/site-languages', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (response.data?.data ?? response.data) as SiteLanguage;
  },

  /**
   * PATCH /admin/site-languages/{id}
   * Update a site language
   */
  updateSiteLanguage: async (
    id: string | number,
    data: UpdateSiteLanguagePayload,
  ): Promise<SiteLanguage> => {
    const response = await axiosInstance.patch(`/admin/site-languages/${id}`, data);
    return (response.data?.data ?? response.data) as SiteLanguage;
  },

  /**
   * DELETE /admin/site-languages/{id}
   * Remove a site language
   */
  deleteSiteLanguage: async (id: string | number): Promise<unknown> => {
    const response = await axiosInstance.delete(`/admin/site-languages/${id}`);
    return response.data;
  },

  /**
   * POST /admin/site-languages/{id}/logo
   * Upload/replace the logo for a site language
   */
  uploadSiteLanguageLogo: async (id: string | number, logo: File): Promise<SiteLanguage> => {
    const formData = new FormData();
    formData.append('logo', logo);

    const response = await axiosInstance.post(`/admin/site-languages/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return (response.data?.data ?? response.data) as SiteLanguage;
  },

  /**
   * PATCH /admin/site-languages/{id}/toggle
   * Flip isEnabled on a site language
   */
  toggleSiteLanguage: async (id: string | number, isEnabled?: boolean): Promise<SiteLanguage> => {
    const response = await axiosInstance.patch(
      `/admin/site-languages/${id}/toggle`,
      isEnabled !== undefined ? { isEnabled } : {},
    );
    return (response.data?.data ?? response.data) as SiteLanguage;
  },
};
